import { SurveyStats } from '@g17eco/types/initiativeStats';
import { DATE, formatDateUTC } from '@utils/date';
import { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { DebouncedSearchBox } from '@g17eco/molecules/search';
import { useMemo, useState } from 'react';
import { naturalSort } from '@utils/index';
import { Link, useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { CompleteButton } from '@features/survey/button/CompleteButton';
import { BulkSurveysMessageModal, SurveyMessageModal } from '@features/message-modal/MessageModal';
import { Action, SurveysEditing } from '../surveys-editing/SurveysEditing';
import { generateSurveysFlexSearchMap } from '../utils';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import './SurveyStatsTable.scss';
import { SURVEY } from '@constants/terminology';
import { BasicAlert } from '@g17eco/molecules/alert';
import { Table, ColumnDef } from '@g17eco/molecules/table';
import { SurveyProgressBar } from '../components/ProgressRow';
import { getPeriodName } from '@utils/dataPeriods';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { SortDropdown, SortField } from './SortDropdown';

interface Props {
  initiativeId: string;
  url: string;
  isUserAdmin: boolean;
  handleReload: () => Promise<void>;
  surveyStats: SurveyStats[];
}

const ProgressBreakdownTitle = () => {
  return (
    <div className='h5 d-flex align-items-center justify-content-between'>
      Progress Breakdown
      <div className='d-flex align-items-center gap-2'>
        <SimpleTooltip text={'Coming soon'}>
          <Button disabled color='link-secondary'>
            <i className='fal fa-gears mr-2 text-md text-themeIconSecondary' />
            Configure table
          </Button>
        </SimpleTooltip>
        <SimpleTooltip text={'Coming soon'}>
          <Button disabled color='primary'>
            <i className='fal fa-file-excel mr-2 text-md text-themeIconSecondary' />
            Download
          </Button>
        </SimpleTooltip>
      </div>
    </div>
  );
};

export const SurveyStatsTable = (props: Props) => {
  const { initiativeId, url, isUserAdmin, handleReload, surveyStats } = props;
  const [editingSurveys, setEditingSurveys] = useState<{ action: Action; surveys: SurveyStats[] }>();
  const [showMessageModal, setShowMessageModal] = useState<string | undefined>(undefined);
  const [showBulkSurveysMessage, setShowBulkSurveysMessage] = useState<boolean>(false);
  const [sortField, setSortField] = useState<SortField>('created');
  const history = useHistory();
  const surveysIndex = useMemo(() => generateSurveysFlexSearchMap(surveyStats), [surveyStats]);

  // Keep FlexSearch for text search only
  const [searchText, setSearchText] = useState('');

  const filteredRows: SurveyStats[] = useMemo(() => {
    let filtered = surveyStats;

    // Apply text search filter
    if (searchText) {
      const result = surveysIndex.search(searchText);
      const matchedIds = new Set(result.map((item) => item.result).flat());
      filtered = surveyStats.filter((survey) => matchedIds.has(survey._id));
    }

    // Sort by selected status field (descending - highest count first)
    /** TODO: Implement both ascending and descending sorting in dropdown, currently dropdown does not support multiple buttons per item */
    return [...filtered].sort((a, b) => b.status[sortField] - a.status[sortField]);
  }, [surveyStats, searchText, sortField, surveysIndex]);

  // Filter surveys for lock/unlock operations
  const handleLockAll = () => {
    setEditingSurveys({ action: Action.Complete, surveys: filteredRows.filter((survey) => !survey.completedDate) });
  };

  const handleUnlockAll = () => {
    setEditingSurveys({ action: Action.Incomplete, surveys: filteredRows.filter((survey) => survey.completedDate) });
  };

  const columns: ColumnDef<SurveyStats>[] = [
    {
      header: 'Date',
      sortingFn: (a, b) => naturalSort(a.original.effectiveDate, b.original.effectiveDate),
      meta: {
        cellProps: {
          style: {
            maxWidth: 140,
            minWidth: 140,
          },
        },
      },
      cell: ({ row }) => {
        return (
          <Link to={`${url}/overview/${row.original._id}`}>
            <span className={'text-truncate'} style={{ display: 'block' }}>
              {`${formatDateUTC(row.original.effectiveDate, DATE.MONTH_YEAR_SHORT)} ${getPeriodName(row.original.period, true)}`}
            </span>
          </Link>
        );
      },
    },
    {
      header: 'Subsidiary',
      cell: ({ row }) => {
        return (
          <Link to={`${url}/overview/${row.original._id}`}>
            <span className={'text-truncate'} style={{ display: 'block' }}>
              {row.original.initiativeName ?? '-'}
            </span>
          </Link>
        );
      },
      meta: {
        cellProps: {
          style: {
            minWidth: 200,
            maxWidth: 200,
          },
        },
      },
    },
    {
      id: 'progress',
      header: () => (
        <div className='d-flex align-items-center justify-content-between'>
          Progress
          <SortDropdown value={sortField} onChange={setSortField} />
        </div>
      ),
      cell: ({ row }) => {
        const { status } = row.original;
        const tooltipContent = (
          <div className='text-start'>
            <div>Unanswered: {status.created}</div>
            <div>Answered: {status.updated}</div>
            <div>Verified: {status.verified}</div>
            <div>Rejected: {status.rejected}</div>
          </div>
        );

        return (
          <SimpleTooltip text={tooltipContent}>
            <SurveyProgressBar className='w-100' progress={status} displayValues={false} />
          </SimpleTooltip>
        );
      },
      meta: {
        cellProps: {
          style: {
            minWidth: 250,
          },
        },
      },
    },
    {
      id: 'mark',
      header: () =>
        isUserAdmin ? (
          <div className='d-flex align-items-center'>
            <Button onClick={handleLockAll} color={'link'} className='btn-xs'>
              <i className='fa-light fa-lock-keyhole me-1' />
              Lock all
            </Button>
            <Button onClick={handleUnlockAll} color={'link'} className='btn-xs'>
              <i className='fa-light fa-lock-keyhole-open me-1' />
              Unlock all
            </Button>
          </div>
        ) : null,
      meta: {
        cellProps: {
          style: {
            width: 100,
            textAlign: 'center',
          },
        },
      },
      cell: ({ row }) => {
        const survey = row.original;
        return (
          <div className='d-flex justify-content-center'>
            <CompleteButton
              classes={{ btn: 'btn-xs mx-0' }}
              isUserAdmin={isUserAdmin}
              survey={survey}
              handleReload={handleReload}
              completedText='Locked'
              notCompletedText='Unlock'
            />
          </div>
        );
      },
    },
    {
      id: 'message',
      header: () => (
        <Button size='sm' color={'link'} className={'btn-xs'} onClick={() => setShowBulkSurveysMessage(true)}>
          <i className='fa-light fa-envelopes me-1' />
          Message
        </Button>
      ),
      meta: {
        cellProps: {
          style: {
            width: 100,
          },
        },
      },
      cell: ({ row }) => (
        <div className='d-flex justify-content-center'>
          <Button
            size={'sm'}
            color={'secondary'}
            className={'btn-xs'}
            onClick={() => setShowMessageModal(row.original._id)}
          >
            <i className='fa-light fa-envelope me-2' />
            Message
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DashboardSectionTitle
        title={SURVEY.CAPITALIZED_PLURAL}
        buttons={[
          <Button
            key='bulk-import-button'
            className='py-1 mr-3'
            onClick={() => history.push(generateUrl(ROUTES.BULK_IMPORTING, { initiativeId }))}
          >
            <span className='lh-base'>
              <i className='fa-light fa-file-import mr-2 fs-5' />
              Bulk import data
            </span>
          </Button>,
          <Button
            key='survey-templates-button'
            className='py-1'
            onClick={() => history.push(generateUrl(ROUTES.SURVEY_TEMPLATES, { initiativeId }))}
          >
            <span className='lh-base'>
              <i className='fa-light fa-layer-group mr-2 fs-5' />
              {SURVEY.CAPITALIZED_SINGULAR} templates
            </span>
          </Button>,
        ]}
      />
      <DashboardSection padding={0} title={<ProgressBreakdownTitle />}>
        <div className='mb-3'>
          <DebouncedSearchBox
            placeholder='Search surveys...'
            onTextChange={setSearchText}
            classNames={{ wrapper: 'w-100' }}
          />
        </div>
        <Table
          pageSize={10}
          columns={columns}
          data={filteredRows}
          noData={<BasicAlert type='secondary'>There are no {SURVEY.PLURAL}.</BasicAlert>}
        />
      </DashboardSection>
      {showMessageModal ? (
        <SurveyMessageModal surveyId={showMessageModal} toggle={() => setShowMessageModal('')} />
      ) : null}
      {editingSurveys ? (
        <SurveysEditing
          surveys={editingSurveys.surveys}
          action={editingSurveys.action}
          handleClose={() => setEditingSurveys(undefined)}
          handleReload={handleReload}
        />
      ) : null}
      {showBulkSurveysMessage ? (
        <BulkSurveysMessageModal surveys={filteredRows} toggle={() => setShowBulkSurveysMessage(false)} />
      ) : null}
    </>
  );
};
